/*
 * Gentle rain, made from two layers:
 * - a bed of pink noise, rolled off above ~3.5 kHz and with the rumble removed: the wash of far-away rain;
 * - patter: dozens of tiny water drops per second (short decaying tones at random pitches), plus a few closer
 *   and lower ones. They are what makes it read as rain rather than as hiss.
 *
 * The same recipe is in android/.../RainSynth.java: change a value in both places.
 */
export const RAIN_SAMPLE_RATE = 22050;

const VOICES = 32;
const BED_LEVEL = 0.8;
const LIGHT_DROPS_PER_SECOND = 55;
const NEAR_DROPS_PER_SECOND = 2.5;

const onePole = (cutoff: number, sampleRate: number): number =>
    1 - Math.exp((-2 * Math.PI * cutoff) / sampleRate);

/** A per-sample rain generator. Returns samples of roughly -1..1, most of them much smaller. */
export const createRainSynth = (
    sampleRate: number = RAIN_SAMPLE_RATE,
    random: () => number = Math.random
): (() => number) => {
    const lowA = onePole(3200, sampleRate);
    const lowB = onePole(6500, sampleRate);
    const high = onePole(280, sampleRate);
    const lightChance = LIGHT_DROPS_PER_SECOND / sampleRate;
    const nearChance = NEAR_DROPS_PER_SECOND / sampleRate;
    const driftChance = 0.25 / sampleRate;
    const driftRate = onePole(0.2, sampleRate);

    let p0 = 0;
    let p1 = 0;
    let p2 = 0;
    let lpA = 0;
    let lpB = 0;
    let hp = 0;
    let drift = 0.92;
    let driftTarget = 0.92;
    // Each drop is a damped resonator: y[n] = c1 * y[n-1] + c2 * y[n-2].
    const c1 = new Float64Array(VOICES);
    const c2 = new Float64Array(VOICES);
    const y1 = new Float64Array(VOICES);
    const y2 = new Float64Array(VOICES);
    let nextVoice = 0;

    const drop = (
        minHz: number,
        maxHz: number,
        minDecay: number,
        maxDecay: number,
        amplitude: number
    ) => {
        const hz = minHz * Math.pow(maxHz / minHz, random());
        const decay = minDecay + (maxDecay - minDecay) * random();
        const w = (2 * Math.PI * hz) / sampleRate;
        const r = Math.exp(-1 / (decay * sampleRate));
        const v = nextVoice;
        nextVoice = (nextVoice + 1) % VOICES;
        c1[v] = 2 * r * Math.cos(w);
        c2[v] = -r * r;
        // Starting on the zero crossing keeps the drop from clicking.
        y1[v] = amplitude * Math.sin(w);
        y2[v] = 0;
    };

    return () => {
        const white = random() * 2 - 1;
        // Paul Kellet's economy pink filter.
        p0 = 0.99765 * p0 + white * 0.099046;
        p1 = 0.963 * p1 + white * 0.2965164;
        p2 = 0.57 * p2 + white * 1.0526913;
        const pink = (p0 + p1 + p2 + white * 0.1848) * 0.25;
        lpA += lowA * (pink - lpA);
        lpB += lowB * (lpA - lpB);
        hp += high * (lpB - hp);
        // The rain leans slightly heavier and lighter every few seconds.
        if (random() < driftChance) driftTarget = 0.84 + random() * 0.16;
        drift += driftRate * (driftTarget - drift);

        if (random() < lightChance * drift) {
            const size = random();
            drop(1400, 5200, 0.0015, 0.005, 0.03 + 0.1 * size * size);
        }
        if (random() < nearChance * drift) {
            drop(650, 1700, 0.008, 0.018, 0.08 + 0.12 * random());
        }
        let drops = 0;
        for (let v = 0; v < VOICES; v++) {
            const y = c1[v] * y1[v] + c2[v] * y2[v];
            y2[v] = y1[v];
            y1[v] = y;
            drops += y;
        }
        return (lpB - hp) * BED_LEVEL * drift + drops;
    };
};

/** `length` samples that loop without a seam: the extra tail is crossfaded over the start. */
export const rainSamples = (
    length: number,
    sampleRate: number = RAIN_SAMPLE_RATE,
    random: () => number = Math.random,
    crossfade: number = Math.min(Math.floor(sampleRate / 2), length)
): Float32Array => {
    const next = createRainSynth(sampleRate, random);
    const out = new Float32Array(length);
    for (let i = 0; i < length; i++) out[i] = next();
    for (let i = 0; i < crossfade; i++) {
        // Equal power: the two ends are unrelated noise, so a linear fade would dip.
        const x = (i + 0.5) / crossfade;
        out[i] =
            out[i] * Math.sin((x * Math.PI) / 2) +
            next() * Math.cos((x * Math.PI) / 2);
    }
    return out;
};
